import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { trackAddToCart } from "@/lib/analytics";
import { showCartToast } from "@/components/CartToast";
import { getProductDisplayName } from "@/lib/productDisplayName";
import { supabase } from "@/integrations/supabase/client";
import { filterVisibleProducts, isHiddenProductSlug, stripHiddenProductLinks, isHiddenProductHref } from "@/lib/hiddenProducts";

export interface CartItem {
  id: string;
  name: string;
  name_ru: string;
  price: number;
  quantity: number;
  image_url: string | null;
  weight_mg: number;
  purity: number;
  addedViaAi?: boolean;
  /** Refreshed from products on cart load. */
  in_stock?: boolean;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "quantity" | "addedViaAi">, addedViaAi?: boolean) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  bonusMode: boolean;
  setBonusMode: (mode: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "biopeptide_cart";
const BONUS_MODE_KEY = "biopeptide_bonus_mode";

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [bonusMode, setBonusModeState] = useState<boolean>(() => {
    try {
      const stored = sessionStorage.getItem(BONUS_MODE_KEY);
      return stored === "true";
    } catch {
      return false;
    }
  });

  // On load: refresh price / names / in_stock from products — stored carts keep stale prices.
  useEffect(() => {
    const ids = items.map((i) => i.id).filter(Boolean);
    if (!ids.length) return;
    let cancelled = false;
    supabase
      .from("products")
      .select("id, slug, name, name_ru, price, in_stock, image_url")
      .in("id", ids)
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        const byId = new Map(data.map((p) => [p.id, p]));
        setItems((prev) =>
          prev.filter((i) => !isHiddenProductSlug(byId.get(i.id)?.slug)).map((i) => {
            const p = byId.get(i.id);
            if (!p) return { ...i, in_stock: false };
            return {
              ...i,
              name: p.name ?? i.name,
              name_ru: p.name_ru ?? i.name_ru,
              price: Number(p.price) || i.price,
              in_stock: p.in_stock ?? i.in_stock,
              image_url: p.image_url ?? i.image_url,
            };
          }),
        );
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    sessionStorage.setItem(BONUS_MODE_KEY, bonusMode ? "true" : "false");
  }, [bonusMode]);

  const setBonusMode = (mode: boolean) => {
    setBonusModeState(mode);
  };

  const addItem = (item: Omit<CartItem, "quantity" | "addedViaAi">, addedViaAi: boolean = false) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) {
        return prev.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...item, quantity: 1, addedViaAi }];
    });
    
    // Track add to cart event in Yandex.Metrika
    trackAddToCart({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
    });

    // Unified cart toast (skip for AI auto-additions to avoid noise)
    // Defer to next tick: separates the synchronous setState from the toast render,
    // preventing React tree crashes from cascading into the cart action.
    if (!addedViaAi) {
      setTimeout(() => {
        try {
          showCartToast(getProductDisplayName(item.name_ru, item.name));
        } catch (err) {
          console.error("showCartToast threw:", err);
        }
      }, 0);
    }
  };

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity } : i))
    );
  };

  const clearCart = () => {
    setItems([]);
    setBonusModeState(false); // Reset bonus mode when cart is cleared
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
        bonusMode,
        setBonusMode,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
